import { useState } from 'react'
import { X, Minus, Plus, ShoppingCart, Trash2, Send, CheckCircle } from 'lucide-react'
import { EmptyState } from './EmptyState'
import { waLink, COMPANY } from '../config'
import { submitInquiry } from '../data/api'

export function CartDrawer({ items, onClose, onUpdateQty, onRemove }) {
  const [form, setForm] = useState({ name: '', contact: '' })
  const [sending, setSending] = useState(false)
  const [ordered, setOrdered] = useState(false)
  const [waUrl, setWaUrl] = useState('')
  const subtotal = items.reduce((sum, item) => sum + item.product.price * item.qty, 0)

  const buildMessage = () =>
    [
      `New retail order — ${COMPANY.legalName}`,
      '',
      `Name: ${form.name}`,
      `Contact: ${form.contact}`,
      '',
      `Items (${items.length}):`,
      items
        .map((item) => `• ${item.product.name} — ${item.qty} × €${item.product.price.toFixed(2)} = €${(item.qty * item.product.price).toFixed(2)}`)
        .join('\n'),
      '',
      `Subtotal: €${subtotal.toFixed(2)}`,
      '',
      'Please confirm availability and delivery details.'
    ].join('\n')

  const handleCheckout = async () => {
    if (sending) return
    setSending(true)

    const url = waLink(buildMessage())
    setWaUrl(url)

    const result = await submitInquiry({
      type: 'order',
      name: form.name,
      contact: form.contact,
      subtotal: subtotal.toFixed(2),
      items: items.map((item) => ({
        name: item.product.name,
        qty: item.qty,
        price: item.product.price.toFixed(2)
      }))
    })
    if (!result.delivered) window.open(url, '_blank')

    setSending(false)
    setOrdered(true)
  }

  return (
    <>
      <div
        onClick={onClose}
        className="fixed inset-0 z-50 bg-brand-navy/40 backdrop-blur-sm transition-opacity"
      />
      <aside className="fixed right-0 top-0 z-50 flex h-full w-full max-w-md flex-col bg-white shadow-drawer">
        <div className="flex items-center justify-between border-b border-brand-slate/10 px-5 py-4">
          <h2 className="flex items-center gap-2 font-display text-xl font-bold text-brand-ink">
            <ShoppingCart className="h-5 w-5 text-brand-gold" />
            Your Cart
          </h2>
          <button onClick={onClose} className="rounded-full p-2 hover:bg-brand-shell" aria-label="Close cart">
            <X className="h-5 w-5 text-brand-slate" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5">
          {items.length === 0 ? (
            <EmptyState context="cart" actionLabel="Shop Retail" onAction={onClose} />
          ) : (
            <ul className="space-y-4">
              {items.map((item) => (
                <li key={item.product.id} className="flex gap-4 rounded-xl bg-brand-shell p-3">
                  <img
                    src={item.product.image}
                    alt={item.product.name}
                    className="h-20 w-20 rounded-lg object-cover"
                  />
                  <div className="flex flex-1 flex-col justify-between">
                    <div>
                      <h4 className="font-semibold text-brand-ink">{item.product.name}</h4>
                      <p className="text-sm text-brand-slate">€{item.product.price.toFixed(2)} / unit</p>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center rounded-lg border border-brand-slate/20 bg-white">
                        <button
                          onClick={() => onUpdateQty(item.product.id, Math.max(1, item.qty - 1))}
                          className="p-2 text-brand-slate hover:text-brand-ink"
                        >
                          <Minus className="h-4 w-4" />
                        </button>
                        <span className="w-8 text-center text-sm font-semibold">{item.qty}</span>
                        <button
                          onClick={() => onUpdateQty(item.product.id, item.qty + 1)}
                          className="p-2 text-brand-slate hover:text-brand-ink"
                        >
                          <Plus className="h-4 w-4" />
                        </button>
                      </div>
                      <button
                        onClick={() => onRemove(item.product.id)}
                        className="rounded-full p-2 text-brand-slate hover:bg-red-50 hover:text-red-600"
                        aria-label="Remove item"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {items.length > 0 && (
          <div className="border-t border-brand-slate/10 p-5">
            {ordered ? (
              <div className="py-2 text-center">
                <CheckCircle className="mx-auto h-8 w-8 text-green-600" />
                <p className="mt-2 font-semibold text-brand-ink">Order sent to our team</p>
                <p className="mt-1 text-xs text-brand-slate">
                  We'll confirm availability and delivery details shortly.
                </p>
                {waUrl && (
                  <a
                    href={waUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="mt-3 inline-flex items-center gap-2 text-xs font-semibold text-[#25D366]"
                  >
                    <Send className="h-3.5 w-3.5" /> Also send via WhatsApp
                  </a>
                )}
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between text-lg font-bold text-brand-ink">
                  <span>Subtotal</span>
                  <span>€{subtotal.toFixed(2)}</span>
                </div>
                <div className="mt-4 space-y-2">
                  <input
                    type="text"
                    required
                    value={form.name}
                    onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                    placeholder="Your name"
                    className="w-full rounded-lg border border-brand-slate/20 bg-white px-3 py-2 text-sm outline-none focus:border-brand-gold focus:ring-2 focus:ring-brand-gold/20"
                  />
                  <input
                    type="text"
                    required
                    value={form.contact}
                    onChange={(e) => setForm((f) => ({ ...f, contact: e.target.value }))}
                    placeholder="Email or phone"
                    className="w-full rounded-lg border border-brand-slate/20 bg-white px-3 py-2 text-sm outline-none focus:border-brand-gold focus:ring-2 focus:ring-brand-gold/20"
                  />
                </div>
                <button
                  onClick={handleCheckout}
                  disabled={sending || !form.name.trim() || !form.contact.trim()}
                  className="mt-3 flex w-full items-center justify-center gap-2 rounded-xl bg-brand-gold py-3 text-sm font-bold text-brand-navy transition-colors hover:bg-brand-gold-light disabled:opacity-60"
                >
                  <Send className="h-4 w-4" />
                  {sending ? 'Sending…' : 'Place Order'}
                </button>
                <p className="mt-2 text-center text-xs text-brand-slate">
                  Sent instantly to our team — no WhatsApp account needed.
                </p>
              </>
            )}
          </div>
        )}
      </aside>
    </>
  )
}
